import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import './ProfileSetup.css';

const ProfileSetup = () => {
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [phone_number, setPhoneNumber] = useState('');
  const [primary_zip_code, setPrimaryZipCode] = useState('');
  const [secondary_zip_code, setSecondaryZipCode] = useState('');
  const [year_of_birth, setYearOfBirth] = useState('');
  const [marital_status, setMaritalStatus] = useState('');
  const [number_of_children, setNumberOfChildren] = useState('');
  const [household_income, setHouseholdIncome] = useState('');
  const [current_club_member, setCurrentClubMember] = useState('');
  const [clubs, setClubs] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsAuthenticated(false);
      setMessage('Please login to continue with profile setup.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [navigate]);

  // Handle zip code input changes with preservation of leading zeros
  const handleZipCodeChange = (e, setterFunction) => {
    const value = e.target.value;
    // Only allow digits and limit to 5 characters
    if (/^\d*$/.test(value) && value.length <= 5) {
      setterFunction(value);
    }
  };

  const handleProfileSetup = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!first_name || !last_name || !phone_number || !primary_zip_code || !year_of_birth || !marital_status || !household_income || !current_club_member) {
      setMessage('Please fill in all required fields.');
      return;
    }

    // Validate phone number and zip codes
    if (!/^\d{10}$/.test(phone_number)) {
      setMessage('Phone number must be 10 digits.');
      return;
    }

    if (!/^\d{5}$/.test(primary_zip_code) || (secondary_zip_code && !/^\d{5}$/.test(secondary_zip_code))) {
      setMessage('Zip codes must be 5 digits.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Ensure zip codes are sent as strings to preserve leading zeros
      await axiosInstance.post('/profile-setup/', {
        first_name,
        last_name,
        phone_number,
        primary_zip_code: primary_zip_code.toString().padStart(5, '0'),
        secondary_zip_code: secondary_zip_code ? secondary_zip_code.toString().padStart(5, '0') : '',
        year_of_birth,
        marital_status,
        number_of_children,
        household_income,
        current_club_member,
        clubs: current_club_member === 'yes' ? clubs : null,
      });
      setMessage('Member information saved');
      setTimeout(() => navigate('/checkout'), 2000);
    } catch (error) {
      console.error('Profile setup failed:', error);
      setMessage('Member info failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const incomeRanges = [
    "100000-150000", "150000-200000", "200000-300000",
    "300000-400000", "400000-500000", "500000-600000",
    "600000-700000", "700000-800000", "800000-900000",
    "900000-1000000", "1000000+"
  ];

  const formatIncomeRange = (range) => {
    if (range === "1000000+") {
      return "$1M+";
    }
    const [low, high] = range.split("-");
    const formattedHigh = high === "1000000" ? "$1M" : `$${(high / 1000).toFixed()}K`;
    return `$${(low / 1000).toFixed()}K - ${formattedHigh}`;
  };

  return (
    isAuthenticated ? (
      <>
        {/* Overlay for the information popup */}
        {isOverlayVisible && (
          <div className="overlay">
            <div className="overlay-content">
              <h2>"Why is this required?"</h2>
              <p>
                At privateIQ, we don't rely on ads. Instead, private clubs partner with us to connect with potential members. Here's how it works:
              </p>
              <ul>
                <li><strong>Anonymous Insights:</strong> Partner clubs receive anonymous user profiles for users in their area—without names, emails, or phone numbers.</li>
                <li><strong>Club Interest:</strong> If a club finds a profile that they believe may make a great member, they can request privateIQ to contact you.</li>
                <li><strong>Your Control:</strong> We notify you if a club expresses interest. You decide whether to share your contact info with the club.</li>
              </ul>
              <p>Our mission is to provide trusted private club information while safeguarding your privacy and enabling clubs to grow responsibly.</p>
              <button
                className="close-overlay-button"
                onClick={() => setIsOverlayVisible(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleProfileSetup} className="profile-setup-form">
          <div className="header-container">
            <h2 className="profile-setup-header">Required Info</h2>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="info-icon"
              onClick={() => setIsOverlayVisible(true)}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
              />
            </svg>
          </div>

          {/* Existing Inputs */}
          <input className="profile-setup-input" type="text" placeholder="First Name" value={first_name} onChange={(e) => setFirstName(e.target.value)} />
          <input className="profile-setup-input" type="text" placeholder="Last Name" value={last_name} onChange={(e) => setLastName(e.target.value)} />
          <input className="profile-setup-input" type="text" placeholder="Phone Number" value={phone_number} onChange={(e) => setPhoneNumber(e.target.value)} />

          {/* Modified zip code inputs to preserve leading zeros */}
          <input
            className="profile-setup-input"
            type="text"
            placeholder="Primary Zip Code"
            value={primary_zip_code}
            onChange={(e) => handleZipCodeChange(e, setPrimaryZipCode)}
          />
          <input
            className="profile-setup-input"
            type="text"
            placeholder="Secondary Zip Code (Optional)"
            value={secondary_zip_code}
            onChange={(e) => handleZipCodeChange(e, setSecondaryZipCode)}
          />

          <select className="profile-setup-select" value={year_of_birth} onChange={(e) => setYearOfBirth(e.target.value)}>
            <option value="">Year of Birth</option>
            {[...Array(new Date().getFullYear() - 1939).keys()].map((num) => (
              <option key={num + 1940} value={num + 1940}>
                {num + 1940}
              </option>
            ))}
          </select>
          <select className="profile-setup-select" value={marital_status} onChange={(e) => setMaritalStatus(e.target.value)}>
            <option value="">Marital Status</option>
            <option value="single">Single</option>
            <option value="married">Married</option>
          </select>
          <select className="profile-setup-select" value={number_of_children} onChange={(e) => setNumberOfChildren(e.target.value)}>
            <option value="">Number of Children</option>
            {[...Array(11).keys()].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          <select className="profile-setup-select" value={household_income} onChange={(e) => setHouseholdIncome(e.target.value)}>
            <option value="">Household Income</option>
            {incomeRanges.map((range) => (
              <option key={range} value={range}>
                {formatIncomeRange(range)}
              </option>
            ))}
          </select>

          {/* Current Club Member Field */}
          <div className="profile-setup-radio-group">
            <label className="profile-setup-label">Are you currently a club member?</label>
            <div className="profile-setup-radio-options">
              <label>
                <input
                  type="radio"
                  name="current_club_member"
                  value="yes"
                  checked={current_club_member === 'yes'}
                  onChange={(e) => setCurrentClubMember(e.target.value)}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="current_club_member"
                  value="no"
                  checked={current_club_member === 'no'}
                  onChange={(e) => setCurrentClubMember(e.target.value)}
                />
                No
              </label>
            </div>
          </div>

          {/* Conditional Club Input */}
          {current_club_member === 'yes' && (
            <textarea
              className="profile-setup-input club-textarea"
              placeholder="List the club(s) you are an active member at (optional)"
              value={clubs}
              onChange={(e) => setClubs(e.target.value)}
              rows="4"
            />
          )}

          <button type="submit" className="profile-setup-button" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
          {message && <p className="profile-setup-message">{message}</p>}
        </form>
      </>
    ) : (
      <p className="profile-setup-message">{message}</p>
    )
  );
};

export default ProfileSetup;